import { useEffect, useState } from 'react'
import { Betrag } from './Betrag'
import { Abrufausfall, useAbruf } from './Abruf'
import { api, betrag as formatiert, periodenname } from './api'
import { belegerlaeuterung, belegtext, bewegungsartname, gesellschaftsname, kontobezeichnung, ohneVortragszeilen, t, anzahlwort } from './sprache'
import type { Sprachschluessel } from './sprache'
import { ohneWert } from './Ohnewert'
import { trifftWert, wertLesen } from './Suche'
import { ariaSortierung, naechste, sortiere } from './Sortierung'
import type { Sortierwahl } from './Sortierung'
import type { Journaltreffer } from './typen'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'

/**
 * Die dritte Sicht des Journals: **Suche** (2.14 Abschnitt 7, H10).
 *
 * 2.14 sieht für das Journal drei Sichten vor — *Belege · Zeilen · ein Beleg*.
 * Die **Zeilen-Sicht** ist die hier: flach, über alle Ordner hinweg, „für die
 * Suche nach einem Betrag oder einem Konto über alle Belege hinweg". Sie wird
 * damit eingelöst und nicht ein zweites Mal erfunden.
 *
 * **Ein Feld, das die Eingabe selbst liest** — dieselbe Regel wie in der
 * Berichtssuche, und dieselbe Maschine (`wertLesen`, `trifftWert`): Ziffern
 * suchen Beträge, Buchstaben suchen Text. Der Anwender soll nicht vorher
 * entscheiden müssen, wonach er sucht; wer eine Differenz jagt, tippt sie ein,
 * und zwar so, wie sie in der Tabelle steht.
 *
 * **Die Fundstelle ist die Zeile, nicht der Beleg.** Eine Wertsuche, die nur
 * Belegsummen kennt, findet die gesuchten 84.000 in einem einundvierzigzeiligen
 * Lieferbeleg nie — und genau dort liegen sie meistens.
 *
 * **Der gesuchte Betrag wird in beiden Vorzeichen getroffen.** Dieselbe
 * Differenz steht auf der einen Seite positiv und auf der anderen negativ; wer
 * sie jagt, weiß vorher nicht, welche Seite er findet. Ein umgekehrter Treffer
 * wird als solcher **benannt** — er ist die Antwort auf eine andere Frage als
 * die gestellte, und das darf nicht untergehen.
 */

const BELEGART: Record<string, Sprachschluessel> = {
  lieferung: 'journal.art.lieferung',
  erfassung: 'journal.art.erfassung',
  vortrag: 'journal.art.vortrag',
  umrechnung: 'journal.art.umrechnung',
  konsolidierung: 'journal.art.konsolidierung',
  storno: 'journal.art.storno',
  zuordnung: 'journal.art.zuordnung',
  ueberleitung: 'journal.art.ueberleitung',
}

/** Was gefragt wird, sobald der Anwender aufgehört hat zu tippen. */
type Suchfrage = { einheit: string; kriterium: { text?: string; wert?: number; toleranz?: number } }

export function Journalsuche({
  einheit,
  nurOrdner,
  ortVon,
  nurPeriode,
  aufTreffer,
  aufBeleg,
}: {
  einheit: string
  /** Auf diesen Ordner beschränkt —  heißt: alle Ordner der Gesellschaft. */
  nurOrdner?: number | null
  /**
   * In welchem Ordner ein Treffer steht.
   *
   * **Der Jahresordner ist das Fehlen eines eigenen Ordners** (R984): Ein
   * Treffer auf einen Beleg, den keine Ablagezeile hält, trägt
   * `ordnerId: null` und steht im Jahresordner seiner Periode. Wer dort
   * steht, soll ihn sehen — ohne diese Übersetzung fiele er durch den Filter.
   */
  ortVon?: (x: Journaltreffer) => number | null
  /** Die Periode des Journals (R986) — `null` heißt: alle. */
  nurPeriode?: number | null
  /** Die Treffer, sobald sie da sind — der Baum zeigt daran seine Zahlen. */
  aufTreffer?: (treffer: Journaltreffer[]) => void
  /** In den Ordner springen und den Beleg aufschlagen (R209). */
  aufBeleg: (treffer: Journaltreffer) => void
}) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('journalsuche:journalsuche')

  const [eingabe, setEingabe] = useState('')
  /** Die Frage, die gerade gilt — `null`, solange nichts zu suchen ist. */
  const [frage, setFrage] = useState<Suchfrage | null>(null)
  const [wahl, setWahl] = useState<Sortierwahl>(null)

  // Die gelesene Zahl — sie entscheidet, wonach gesucht wird, und beschriftet
  // hinterher, welcher Treffer vorzeichenumgekehrt ist.
  const zahl = wertLesen(eingabe)

  useEffect(() => {
    const text = eingabe.trim()
    if (text.length < 2 && zahl === null) {
      setFrage(null)
      aufTreffer?.([])
      return
    }

    // **Erst tippen lassen, dann fragen.** Ein Ruf je Tastendruck würde über
    // Millionen Journalzeilen scannen, während der Anwender noch schreibt.
    const uhr = setTimeout(
      () =>
        setFrage({ einheit, kriterium: zahl ? { wert: zahl.wert, toleranz: zahl.toleranz } : { text } }),
      350,
    )

    return () => clearTimeout(uhr)
  }, [eingabe, einheit, zahl?.wert, zahl?.toleranz])

  // Die Treffer sind der Inhalt — ein Hauptabruf (R1211). Scheitert die Suche,
  // steht „Ließ sich nicht laden" statt „keine Zeile im Journal" (R1210).
  const suche = useAbruf(frage ? () => api.journalsuche(frage.einheit, frage.kriterium) : null, [frage])
  const treffer = suche.wert
  const laeuft = suche.laedt

  // Der Baum zählt die Treffer, sobald sie da sind.
  useEffect(() => {
    if (suche.wert !== null) {
      aufTreffer?.(suche.wert)
    }
  }, [suche.wert])

  function partnername(schluessel: string | null): string {
    if (schluessel === null) return ohneWert('weitere')
    return gesellschaftsname(schluessel)
  }

  /** Ob ein Treffer die gesuchte Zahl mit umgekehrtem Vorzeichen trägt. */
  function umgekehrt(x: Journaltreffer): boolean {
    return zahl !== null && trifftWert(x.betrag, zahl.wert, zahl.toleranz) === 'umgekehrt'
  }

  const spalten = [
    { id: 'beleg', kopf: t('vorgang.spalte.nummer') },
    { id: 'name', kopf: t('beleg.name') },
    { id: 'periode', kopf: t('journal.periode') },
    { id: 'konto', kopf: t('beleg.spalte.konto') },
    { id: 'bewegungsart', kopf: t('beleg.spalte.bewegungsart') },
    { id: 'partner', kopf: t('beleg.spalte.partner') },
    { id: 'betrag', kopf: t('vorgang.spalte.betrag'), zahl: true },
  ]

  function schluessel(x: Journaltreffer, spalte: string): string | number | null {
    switch (spalte) {
      case 'beleg':
        return x.nummer
      case 'name':
        // Sortiert wird nach dem gespeicherten Namen, nicht nach dem gelesenen
        // (7.2 Abschnitt 21, R839): Mit Monatsnamen stünde „… April 2026" vor
        // „… Januar 2026".
        return x.name
      case 'periode':
        return x.periode
      case 'konto':
        return kontobezeichnung(x.konto)
      case 'bewegungsart':
        return bewegungsartname(x.bewegungsart)
      case 'partner':
        return x.icPartner === null ? null : partnername(x.icPartner)
      default:
        return x.betrag
    }
  }

  /*
    **Der Baum filtert die Treffer.** Wer links einen Ordner anklickt, sieht
    die Treffer darin — und der Ordner bleibt ein Ort statt einer Spalte.
    Gesucht wird trotzdem über alle: Der Baum sagt daneben, wie viele wo
    liegen, und genau das ist die Auskunft, die man beim Suchen braucht.
  */
  // Eine Vortragszeile ist kein Treffer (R1063): Sie trägt den Stand des
  // Vormonats und wäre bei jedem Betrag der Treffer des Vormonats noch einmal.
  const imMonat = ohneVortragszeilen(treffer ?? []).filter((x) => !nurPeriode || x.periode === nurPeriode)
  const imOrdner =
    nurOrdner === null || nurOrdner === undefined
      ? imMonat
      : imMonat.filter((x) => (ortVon ? ortVon(x) : x.ordnerId) === nurOrdner)

  const gezeigt = sortiere(imOrdner, wahl, schluessel)
  const umgekehrte = gezeigt.filter(umgekehrt).length

  return (
    <>
      {/*
        **Nur das Suchfeld — die Gesellschaft steht im Baum daneben.**

        Die Suche hatte bis zum 2026-09-12 eine eigene Leiste und eine eigene
        Gesellschaftswahl, und das Ordnerbrett verschwand dabei ganz: Jeder
        Ordner wurde zu einer Zelle in der ersten Spalte, gleichwertig zu
        Konto und Betrag daneben. Damit war er kein *Ort* mehr, sondern ein
        Merkmal. Jetzt steht die Suche **im** Brett, der Baum bleibt links, und
        die Treffer stehen dort, wo sonst die Belege eines Ordners stehen.
      */}
      <div className="feld breit suchfeld">
        <label htmlFor="journalsuchfeld">{t('suche.journal')}</label>
        <input
          id="journalsuchfeld"
          type="search"
          value={eingabe}
          placeholder={t('suche.journal.platzhalter')}
          autoComplete="off"
          onChange={(e) => setEingabe(e.target.value)}
        />
      </div>

      {/*
        **Was gerade gesucht wird, steht da.** Eine Suche, die stumm
        entscheidet, ob sie Text oder Zahlen sucht, lässt den Anwender rätseln,
        warum „1200" keine Konten findet.
      */}
      <p className="erlaeuterung">
        {zahl !== null
          ? t('suche.alsWert', {
              wert: formatiert(zahl.wert),
              toleranz: formatiert(zahl.toleranz),
            })
          : t('suche.alsText')}
      </p>

      {laeuft && <p className="hinweis">{t('vorgang.laedt')}</p>}
      {suche.ausgefallen && <Abrufausfall abruf={suche} />}

      {/*
        **Leer im Ordner ist nicht leer im Journal.** Bis zum 2026-09-12 stand
        hier „in dieser Gesellschaft keine Zeile", auch wenn zwei Treffer im
        Nachbarordner lagen — das ist die falsche Auskunft (R144), und sie
        bringt den Anwender dazu, eine Suche aufzugeben, die etwas gefunden
        hat. Wo die Treffer liegen, sagt der Baum links; dass es sie gibt,
        muss hier stehen.
      */}
      {!laeuft && treffer !== null && imOrdner.length === 0 && (
        <p className="hinweis">
          {(treffer?.length ?? 0) > 0
            ? t('suche.journal.leer.ordner', { anzahl: anzahlwort(treffer.length) })
            : t('suche.journal.leer')}
        </p>
      )}

      {!laeuft && treffer !== null && imOrdner.length > 0 && (
        <>
          <p className="probe stimmt">
            {t(imOrdner.length === 1 ? 'suche.journal.treffer.einer' : 'suche.journal.treffer', {
              anzahl: anzahlwort(imOrdner.length),
            })}
            {umgekehrte > 0 && ` ${t('suche.journal.umgekehrt.zahl', { anzahl: anzahlwort(umgekehrte) })}`}
          </p>

          <div className="tabellenrahmen belegliste trefferliste">
            <table ref={spaltenrahmen} className="ziehbaretabelle">
              <Spaltenmassleiste masse={spaltenmasse} />
              <thead>
                <tr>
                  {spalten.map((s) => (
                    <th
                      key={s.id}
                      className={s.zahl ? 'zahl' : undefined}
                      aria-sort={ariaSortierung(wahl, s.id)}
                    >
                      <Spaltenkopf
                        kopf={s.kopf}
                        wahl={wahl}
                        spalte={s.id}
                        aufKlick={() => setWahl((v) => naechste(v, s.id))}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {gezeigt.map((x) => (
                  <tr
                    key={x.zeileId}
                    tabIndex={0}
                    onClick={() => aufBeleg(x)}
                    onKeyDown={(e) => e.key === 'Enter' && aufBeleg(x)}
                  >
                    <td className="kennung">
                      <span className="belegnummer">{x.nummer}</span>
                      {/* Dieselbe Regel wie in der Belegliste (R391a): der Typ
                          steht da, die Belegart nur, wenn sie mehr sagt. Eine
                          Liste, die anders beschriftet als die andere, wären
                          zwei Sprachen für dieselbe Sache. */}
                      <span className={`belegtyp ${x.belegtyp}`}>
                        {t(`belegtyp.${x.belegtyp}` as Sprachschluessel)}
                      </span>
                      {x.belegartSagtMehr && (
                        <span className={`belegart ${x.belegart}`}>
                          {BELEGART[x.belegart] ? t(BELEGART[x.belegart]!) : x.belegart}
                        </span>
                      )}
                    </td>
                    <td>
                      {/* Der gespeicherte Text wird mit Namen gelesen, nicht umgeschrieben
                          (R838) — und mit Monat und Jahr (7.2 Abschnitt 21, R839). */}
                      <div>{x.name ? belegtext(x.name) : '—'}</div>
                      {/* Ohne die Zeilenzahl des Datenimports (R1063). */}
                      {belegerlaeuterung(x.erlaeuterung, x.name) && (
                        <div className="erlaeuterung">{belegerlaeuterung(x.erlaeuterung, x.name)}</div>
                      )}
                    </td>
                    <td>{periodenname(x.periode)}</td>
                    <td>{kontobezeichnung(x.konto)}</td>
                    <td>{bewegungsartname(x.bewegungsart)}</td>
                    <td>{partnername(x.icPartner)}</td>
                    <td className={`zahl ${x.betrag < 0 ? 'minus' : ''}`}>
                      <Betrag wert={x.betrag} waehrung={x.waehrung} />
                      {/*
                        **Der umgekehrte Treffer wird benannt.** Er beantwortet
                        eine andere Frage als die gestellte — wer 84.000 sucht
                        und −84.000 findet, hat die Gegenbuchung vor sich und
                        soll das wissen, statt es zu übersehen.
                      */}
                      {umgekehrt(x) && (
                        <span className="aufrissmarker">{t('suche.journal.umgekehrt')}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  )
}

/**
 * Ein Spaltenkopf, der sortiert.
 *
 * **Der Pfeil steht nur an der sortierten Spalte** — ein Pfeil an jeder Spalte
 * wäre ein Muster und keine Auskunft. Dass die anderen auch klickbar sind,
 * sagt der Mauszeiger und der Hilfetext.
 */
export function Spaltenkopf({
  kopf,
  wahl,
  spalte,
  aufKlick,
}: {
  kopf: string
  wahl: Sortierwahl
  spalte: string
  aufKlick: () => void
}) {
  const aktiv = wahl?.spalte === spalte

  return (
    <button type="button" className="spaltenkopf" title={t('sortierung.hilfe')} onClick={aufKlick}>
      {kopf}
      <span className={`pfeil${aktiv ? ' aktiv' : ''}`} aria-hidden="true">
        {aktiv ? (wahl!.richtung === 'auf' ? '▲' : '▼') : '▾'}
      </span>
    </button>
  )
}
